import React, { useEffect } from "react";
import { Candidate, Tabs } from "./candidates.types";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";
import CustomInput from "@components/CustomInput/CustomInput";
import { useForm } from "react-hook-form";
import { ListFilter, Search } from "lucide-react";
import JobDetails from "./jobDetails";
import { getSubmittedCandidates } from "@endpoints/hiringCompany/requirements";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@utils/theme";
import dayjs from "dayjs";
import { transformApplicationData } from "./candidates.utils";

const Candidates: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { pathname } = useLocation();

  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      activeTab: Tabs?.Candidate,
      loading: true,
      data: [],
    },
  });

  const fields = watch();

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const response = await getSubmittedCandidates(params?.id);
      if (params?.agentId) {
        setValue(
          "data",
          response?.data?.filter((x) => x?.job?.createdBy === params?.agentId)
        );
        return;
      }
      setValue("data", response?.data);
    } catch (error) {
    } finally {
      setValue("loading", false);
    }
  };

  return (
    <div className="flex flex-col rounded-lg bg-white w-full flex-grow">
      {/* Header */}
      <div className="flex  items-center min-h-20 justify-between w-full gap-8 my-3">
        {fields?.activeTab === Tabs.Candidate && (
          <div className="flex w-full gap-3 bg-[#F1F5F9] rounded-lg  px-3 py-3">
            <div className="relative w-2/3 ">
              {/* @ts-ignore */}
              <CustomInput
                name="search"
                control={control}
                className="border-none"
                errors={errors}
                placeholder="Search by job title"
                renderRight={<Search color="#6F6F6F" width={16} />}
              />
            </div>
            <div className="relative w-1/3">
              {/* @ts-ignore */}
              <CustomInput
                name="search"
                control={control}
                className="border-none"
                errors={errors}
                placeholder="Search by job title"
                renderRight={<ListFilter color="#6F6F6F" width={16} />}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-1 overflow-x-auto no-scrollbar">
        {fields?.loading ? (
          <div className="flex flex-1 flex-col rounded-lg bg-white w-full flex-grow p-6 justify-center items-center">
            <LoadingAnimation color={colors.PrimaryBlack} />
          </div>
        ) : (
          fields?.activeTab === Tabs.Candidate && (
            <div className="w-full">
              <table className="w-full table-auto text-sm">
                <thead className="sticky top-0 bg-white z-10 border-b border-[#E0E0E0]">
                  <tr>
                    <th className="py-3 text-left px-4 text-xs font-semibold text-light-black">
                      Candidate name
                    </th>
                    <th className="py-3  text-xs text-left pl-4 font-semibold text-light-black">
                      Reference
                    </th>
                    <th className="py-3 text-xs text-left pl-4 font-semibold text-light-black">
                      Applied date
                    </th>
                    <th className="py-3 text-xs text-left pl-4 font-semibold text-light-black">
                      Interview date
                    </th>
                    <th className="py-3 text-xs text-left pl-4 font-semibold text-light-black">
                      Status
                    </th>
                    <th className="py-3 text-xs text-left pl-4 font-semibold text-light-black">
                      Skills matched
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fields?.data?.map((candidate: Candidate, idx: number) => (
                    <tr
                      key={idx}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() =>
                        navigate(`/requirements/job-applicant-detail?${new URLSearchParams({
                          requirementId: params.id,
                          applicationId: candidate.appliedJobInfo._id,
                        })}`, {
                          state: {
                            item: {
                              jobRequirementId: params?.id,
                              ...transformApplicationData(candidate),
                            },
                          },
                        })
                      }
                    >
                      <td className="px-4 py-3 flex items-center gap-2">
                        <img
                          src={candidate?.candidate?.profilePic}
                          alt={candidate.candidate?.firstName}
                          className="w-8 h-8 rounded-full"
                        />
                        <span className="font-medium text-xs text-[#6F6F6F]">
                          {candidate.candidate?.firstName}{" "}
                          {candidate.candidate?.lastName}
                        </span>
                      </td>
                      <td className="px-4 py-3  font-medium text-xs text-[#6F6F6F]">
                        {candidate.candidate.refNo}
                      </td>
                      <td className="px-4 py-3  font-medium text-xs text-[#6F6F6F]">
                        {dayjs(candidate.appliedJobInfo.createdAt).format(
                          "Do MMMM YYYY"
                        )}
                      </td>
                      <td className="px-4 py-3  font-medium text-xs text-[#6F6F6F]">
                        -
                      </td>
                      <td className="px-4 py-3  font-medium text-xs text-[#6F6F6F] capitalize">
                        {candidate.candidate?.status?.toLowerCase()}
                      </td>
                      <td className="px-4 py-3  font-medium text-xs text-[#6F6F6F]">
                        {candidate.skillsMatched?.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
        {fields?.activeTab === Tabs.JobDetails && <JobDetails />}
      </div>
    </div>
  );
};

export default Candidates;
